/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery } from '@apollo/client';
import {
  useEffect, useState, useMemo, useCallback, useRef,
} from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  ServiceType,
  addService,
  clearAllSelectedServices,
  removeService,
} from '../features/serviceTypes/serviceTypesSlice';
import styles from '../sass/components/RegularServiceList.module.scss';
import { GET_SERVICE_PRICES, GET_USER_BY_ID } from '../util/gql';
import { sortServices } from '../util/sortServices';
import Button from './Button';
import Loader from './Loader';
import ProductCard from './ProductCard';
import { ReactComponent as Arrow } from '../images/chevron_down_small.svg';
import { ServiceType as ServiceTypeName } from '../interfaces/WorkOrder';
import {
  isSubscribeTo, pairedKey, ServiceNameKeys,
} from '../util/serviceKeys';
import {
  selectBestSlotAdjustmentLabel,
  selectMobileServiceFeeConfig,
  selectBestSlotAdjustmentPercent,
  selectPaymentPreference,
  selectPrepayDiscountPercent,
} from '../features/booking/bookingSlice';
import {
  fromPriceDollars,
  mobileServiceFeeHint,
  mobileServiceFeeNextVisitCents,
} from '../util/bookingPricing';
import { cleanServiceName } from './BookingSummaryBar';
import AperioPlanOptions from './AperioPlanOptions';
import { aperioPlanOptions } from '../util/aperioPlans';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { serviceNameText, untranslatedLang } from '../util/serviceText';

export interface ServiceListProps {
  disabled: boolean,
}

interface RegularServiceListProps {
  /**
   * Called after a plan card's "Choose ..." has put that plan in the basket,
   * so the step can move on (routes/Services.tsx). Only the Aperio preset's
   * plan cards use it.
   */
  onPlanContinue?: () => void;
}

export default function RegularServiceList({
  onPlanContinue,
}: RegularServiceListProps = {}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // The Aperio preset shows its three plans as short cards with this
  // vehicle's price (AperioPlanOptions) instead of ProductCards. Every other
  // tenant keeps the ProductCard list exactly as it was.
  const planOffer = normalizeThemePreset(useTenant()?.themePreset) === 'aperio';
  const generalRef = useRef<HTMLDivElement | null>(null);
  const { id: vehicleId, userVehicleId, model: vehicleModel } = useAppSelector(
    (state) => state.car,
  );
  const prepayDiscountPercent = useAppSelector(selectPrepayDiscountPercent);
  const paymentPreference = useAppSelector(selectPaymentPreference);
  const countryCode = useAppSelector(
    (state) => state.serviceLocation.serviceAddress?.countryCode,
  );
  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const selectedServices = useAppSelector((state) => (
    state.serviceTypes.services
  ));
  const { id: customerId } = useAppSelector((state) => state.customer);
  const bestSlotAdjustmentPercent = useAppSelector(
    selectBestSlotAdjustmentPercent,
  );
  const bestSlotAdjustmentLabel = useAppSelector(
    selectBestSlotAdjustmentLabel,
  );
  // The fee a plan card quotes is the one the next visit is charged: for
  // tenants whose fee escalates with use it depends on how many visits this
  // vehicle already had in its plan year.
  const feeConfig = useAppSelector(selectMobileServiceFeeConfig);
  const mobileServiceFeeCents = mobileServiceFeeNextVisitCents(feeConfig);
  const feeHint = mobileServiceFeeHint(feeConfig);
  const [loadPrices, {
    loading, error, data,
  }] = useLazyQuery(GET_SERVICE_PRICES, {
    fetchPolicy: 'network-only',
  });

  const [loadUserInformation, {
    data: userInfo,
  }] = useLazyQuery(GET_USER_BY_ID);

  const [showGeneral, setShowGeneral] = useState(false);

  // A different vehicle, country or customer prices differently, so the
  // basket starts over - but not on the way into the step: Back, Forward
  // and a reload keep the service or plan already chosen (QA 2026-09-25 #9).
  const priceKey = `${countryCode}|${vehicleId}|${customerId}|${userVehicleId}`;
  const lastPriceKey = useRef(priceKey);
  useEffect(() => {
    if (countryCode !== '' && vehicleId !== null) {
      if (lastPriceKey.current !== priceKey) {
        dispatch(clearAllSelectedServices());
      }
      lastPriceKey.current = priceKey;

      loadPrices({
        variables: {
          vehicleId,
          countryCode,
          customerId,
          userVehicleId,
        },
      });
    }
  }, [countryCode, vehicleId, loadPrices, customerId, userVehicleId, priceKey]);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 1) {
      const userId = customerId || currentUser.id;
      loadUserInformation({ variables: { userId } });
    }
  }, [customerId, currentUser, loadUserInformation]);

  const sortedServices = sortServices(data?.getServicePrices || []);

  const hiddenSubscriptionPlans = useMemo(
    () => sortedServices.filter(
      (s) => s.serviceTypeName === ServiceTypeName.subscription
          && s.active === false,
    ),
    [sortedServices],
  );

  const visibleServices = useMemo(
    () => sortedServices.filter((s) => s.active !== false),
    [sortedServices],
  );

  const {
    subscriptionServices,
    generalServices,
    hasSubscriptions,
  } = useMemo(() => {
    const subs = visibleServices.filter(
      (s) => s.serviceTypeName === ServiceTypeName.subscription,
    );
    const general = visibleServices.filter(
      (s) => s.serviceTypeName !== ServiceTypeName.subscription,
    );
    return {
      subscriptionServices: subs,
      generalServices: general,
      hasSubscriptions: subs.length > 0,
    };
  }, [visibleServices]);

  useEffect(() => {
    setShowGeneral(!hasSubscriptions);
  }, [hasSubscriptions]);

  const attachHiddenPlanFor = useCallback(
    (selectedSub: ServiceType) => {
      const targetKey = pairedKey(selectedSub.nameKey);
      if (!targetKey) return;
      const match = hiddenSubscriptionPlans.find(
        (s) => s.nameKey === targetKey,
      );
      if (!match) return;

      if (selectedServices.some((s) => s.id === match.id)) return;

      dispatch(addService(match));
    },
    [dispatch, hiddenSubscriptionPlans, selectedServices],
  );

  // Detach the hidden plan when the subscribe_to service is removed
  const detachHiddenPlanFor = useCallback(
    (removedSub: ServiceType) => {
      const targetKey = pairedKey(removedSub.nameKey);
      if (!targetKey) return;

      const match = hiddenSubscriptionPlans.find(
        (s) => s.nameKey === targetKey,
      );
      if (!match) return;

      if (selectedServices.some((s) => s.id === match.id)) {
        dispatch(removeService(match.id));
      }
    },
    [dispatch, hiddenSubscriptionPlans, selectedServices],
  );

  const add = (service: ServiceType) => {
    if (userInfo?.getUserById?.vendorId === 1) {
      dispatch(addService(service));
    } else {
      dispatch(clearAllSelectedServices());
      dispatch(addService(service));
    }
    if (service.serviceTypeName === ServiceTypeName.subscription
      && isSubscribeTo(service.nameKey)) {
      attachHiddenPlanFor(service);
    }
  };

  const remove = (service: ServiceType) => {
    dispatch(removeService(service.id));
    if (service.serviceTypeName === ServiceTypeName.subscription
      && isSubscribeTo(service.nameKey)) {
      detachHiddenPlanFor(service);
    }
  };

  if (!vehicleId) {
    return (
      <div className={styles.empty}>
        {t('services.selectVehicle')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.empty}>
        <Loader />
        {t('common.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.empty}>
        {t('services.loadError')}
        <Button
          className={styles.editButton}
          onClick={() => {
            loadPrices({
              variables: {
                countryCode,
                vehicleId,
              },
            });
          }}
        >
          {t('common.refresh')}
        </Button>
      </div>
    );
  }

  if (!sortedServices.length) {
    return <div className={styles.empty}>{t('services.empty')}</div>;
  }

  /** The bundled plan for a "subscribe to" onboarding service, if any. */
  const planFor = (item: ServiceType) => {
    if (!isSubscribeTo(item.nameKey)) return null;
    const planKey = pairedKey(item.nameKey);
    const plan = hiddenSubscriptionPlans.find((s) => s.nameKey === planKey);
    if (!plan) return null;
    return {
      price: plan.value,
      frequency: planKey === ServiceNameKeys.subscriptionPlanMonthly
        ? 'monthly' as const
        : 'annual' as const,
      name: plan.name,
      priceNote: plan.priceNote ?? null,
    };
  };

  /** Monthly cost of a subscription card, used to pick the one Best Value. */
  const monthlyCost = (item: ServiceType): number | null => {
    const plan = planFor(item);
    if (!plan) return null;
    return plan.frequency === 'annual' ? plan.price / 12 : plan.price;
  };
  const bestValueId = subscriptionServices.reduce<{
    id: number | null; cost: number;
  }>((best, item) => {
    const cost = monthlyCost(item);
    if (cost === null) return best;
    return cost < best.cost ? { id: item.id, cost } : best;
  }, { id: null, cost: Number.POSITIVE_INFINITY }).id;

  /** Brings the one-time services into view, clear of the site bar. */
  const showPayPerVisit = () => {
    setShowGeneral(true);
    window.setTimeout(() => {
      const el = generalRef.current;
      if (!el) return;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 84,
        behavior: 'smooth',
      });
    }, 0);
  };

  // Aperio preset: the three plans as short cards (AperioPlanOptions). Empty
  // when the catalogue has none of them, and then the ProductCards render.
  const planOptions = planOffer
    ? aperioPlanOptions(
      subscriptionServices,
      hiddenSubscriptionPlans,
      feeConfig,
      prepayDiscountPercent,
    )
    : [];

  const renderPlanOptions = () => (
    <div className={styles.cardList}>
      <AperioPlanOptions
        options={planOptions}
        prepayDiscountPercent={prepayDiscountPercent}
        vehicleModel={vehicleModel}
        countryCode={countryCode}
        paymentPreference={paymentPreference}
        onChoose={(option) => {
          const item = option.onboarding;
          const planInBasket = selectedServices.some(
            (s) => s.id === option.plan.id,
          );
          if (!planInBasket
            || !selectedServices.some((s) => s.id === item.id)) add(item);
          onPlanContinue?.();
        }}
        onPayPerVisit={showPayPerVisit}
      />
    </div>
  );

  const renderCard = (item: ServiceType) => {
    const name = serviceNameText(item);
    return (
      <div key={item.id} className={styles.cardList}>
        <ProductCard
          key={item.id}
          details={item.description}
          detailsLang={untranslatedLang(false)}
          subDescription={item.subDescription}
          title={planFor(item) ? cleanServiceName(name.text) : name.text}
          titleLang={untranslatedLang(name.isTranslated)}
          bestValue={item.id === bestValueId}
          price={item.value}
          isEstimate={item.isEstimate}
          selected={selectedServices.some((service) => service.id === item.id)}
          onSelect={() => add(item)}
          onRemove={() => remove(item)}
          discountedPrice={item.discountedValue}
          serviceTypeName={item.serviceTypeName}
          fromPrice={fromPriceDollars(item, bestSlotAdjustmentPercent)}
          fromPriceLabel={bestSlotAdjustmentLabel}
          subscriptionPlan={planFor(item)}
          mobileServiceFeeCents={mobileServiceFeeCents}
          mobileServiceFeeHint={feeHint}
          mobileServiceFeeIncludedFirstVisit={
            !!feeConfig.planStartMobileFeeIncluded
          }
        />
      </div>
    );
  };

  // The plan cards' fine print already says "before tax".
  const plansAsOptions = planOptions.length > 0;

  return (
    <>
      {hasSubscriptions && (plansAsOptions
        ? renderPlanOptions()
        : subscriptionServices.map(renderCard))}
      {!plansAsOptions && (
        <p className={styles.taxNote}>{t('services.taxNote')}</p>
      )}

      {hasSubscriptions ? (
        <>
          <div className={styles.cardList} ref={generalRef}>
            <div className={styles.dropdownCard}>
              <button
                type="button"
                className={styles.dropdownHeaderAsCard}
                onClick={() => setShowGeneral((v) => !v)}
                aria-expanded={showGeneral}
              >
                <div className={cx(styles.cardLeft,
                  { [styles.expanded]: showGeneral })}
                >
                  <Arrow className={cx(styles.dropdownChevron,
                    { [styles.expandedArrow]: showGeneral })}
                  />
                </div>

                <div className={styles.cardDescription}>
                  <h4 className={styles.cardTitle}>
                    {t('services.oneTimeTitle')}
                  </h4>
                  <p className={styles.cardSubtitle}>
                    {t('services.optionCount', {
                      count: generalServices.length,
                    })}
                  </p>
                </div>
              </button>
            </div>
          </div>

          {showGeneral && (
          <div className={styles.dropdownBody}>
            {generalServices.map(renderCard)}
          </div>
          )}
        </>
      ) : (
        generalServices.map(renderCard)
      )}
    </>
  );
}
